import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import styles from './index.module.scss';
import { tableStyles } from 'css/tableStyles';
import _ from 'lodash';
import { DATE_FORMATS, ORDER_ITEM_STATUS, ORDER_STATUS, formatDate, sanitizeInventoryItemsWithInventoryAddress, showWarning, sortAlphaNum } from 'utils';
import EditActionForTable from 'components/EditActionForTableData';
import Currency from 'components/Currency/Currency';
import CustomLink from 'components/TableLink';
import MarkOrderItemPacked from './markOrderItemPacked';

const propsEqual = (prevProps, nextProps) => {
  return _.isEqual(prevProps, nextProps);
};

const OrderItems = props => {
  const {
    items,
    itemsSelectable,
    selectedRowsForRefund,
    setSelectedForRefundRows,
    handleUpdateOrderItemQuantity,
    setOrderPackedModal,
    refresh,
    order,
  } = props;

  // `items` is expected to be an array of order items, but some APIs return an object like:
  // { categoryImage, items: [...] }. Normalize here to avoid runtime crashes.
  const itemsArray = useMemo(() => {
    if (Array.isArray(items)) return items;
    if (items && Array.isArray((items as any).items)) return (items as any).items;
    return [];
  }, [items]);

  const categoryImage = !Array.isArray(items) ? (items as any)?.categoryImage : undefined;

  //We are creating this state because we need to pass these ids in table component to make background green
  const [newlyPackedItemIds, setNewlyPackedItemIds] = useState([]) as any;

  const getItemCost = (row) => {
    return _.get(row, 'item.weightedAvgCost', 0) * _.get(row, 'count', 1);
  };

  useEffect(() => {
    const packedItems = itemsArray.filter(item => item?.status && (item.status === ORDER_ITEM_STATUS.PACKED));
    if (packedItems.length === itemsArray.length &&
      (order.status === ORDER_STATUS.PAID || order.status === ORDER_STATUS.PENDING_PAYMENT || order.status === ORDER_STATUS.UNPACKED)) {
      setOrderPackedModal(true);
    }
  }, [itemsArray]);

  const debouncedRefresh = useCallback(_.debounce(refresh, 2000), []);
  
  const itemTableColumns = useMemo(() => [
    {
      name: '',
      selector: '',
      width: '150px',
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <MarkOrderItemPacked 
          item={row} 
          debouncedRefresh={debouncedRefresh}
          newlyPackedItemIds={newlyPackedItemIds}
          setNewlyPackedItemIds={setNewlyPackedItemIds}
        />
      ),
    },
    {
      name: 'Slot No.',
      selector: 'slotNo',
      width: '100px',
      format: row => row.slotNo ? row.slotNo : '-',
      sortable: true,
      sortFunction: sortAlphaNum,
    },
    {
      name: 'Item ID',
      selector: 'objectId',
      width: '120px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/inventory-items/${row.item.objectId}`}
        />
      ),
    },
    {
      name: 'Item Name',
      selector: 'name',
      width: '300px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <div
          className={styles['item']}
          onClick={() => window.location.assign(`/inventory-items/${row.item.objectId}`)}
        >
          <img src={row.item?.image} alt={row.item?.name} />
          <div>{row.item?.name || '-'}</div>
        </div>
      ),
    },
    {
      name: 'Item Totals',
      selector: 'price',
      // eslint-disable-next-line react/display-name
      format: (row) => row.price ? <Currency amount={row.price} /> : '-',
    },
    {
      name: 'Item Cost',
      selector: 'item',
      // eslint-disable-next-line react/display-name
      format: (row) => row.item?.weightedAvgCost ? <Currency amount={getItemCost(row)} /> : '-',
    },
    {
      name: 'Quantity',
      selector: 'count',
      format: row => _.isNumber(row.count) ? row.count : '-',
      conditionalCellStyles: [
        {
          when: row => row.count > 1,
          style: {
            fontSize: '18px',
            fontWeight: 'bolder',
          },
        },
      ],
    },
    {
      name: 'Unit',
      selector: 'unit.description',
      format: row => row.item?.unit?.description ? row.item?.unit.description : '-',
    },
    {
      name: 'Inventory Address',
      selector: 'inventoryAddress',
      sortable: true,
      format: row => row.inventoryAddress ?? '-',
      width: '150px',
    },
    {
      name: 'Refunded Quantity',
      selector: 'refundedCount',
      format: row => row.refundedCount ? row.refundedCount : '0',
    },
    {
      name: 'Refund Reason',
      selector: 'refundReason',
      width: '200px',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <div className={styles['refund-reason']}>{row.refundReason ? row.refundReason : 'N/A'}</div>
      ),
    },
    {
      name: 'Discount (%)',
      selector: 'discountPercent',
      format: row => row.item?.discountPercent ? row.item?.discountPercent : '-',
    },
    {
      name: 'Status',
      selector: 'status',
      width: '150px',
      format: row => row.status ? row.status : '-',
    },
    {
      name: 'Packed At',
      selector: 'packedAt',
      width: '175px',
      format: row => row.packedAt ? formatDate(row.packedAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A) : '-',
    },
    {
      name: 'Action',
      selector: '',
      width: '300px',
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'d-flex ml-1'}>
          <EditActionForTable
            onClick={() => handleUpdateOrderItemQuantity(row.objectId, row.count)}
          />
        </div>
      ),
    },
  ], [newlyPackedItemIds]);

  const setSelectedItems = useCallback((data: Record<string, any>) => {
    const { selectedRows } = data;
    const selectedRecords = selectedRows.map((row: Record<string, any>) => {
      if (row.status !== ORDER_ITEM_STATUS.REFUNDED) {
        let item: Record<string, any> = _.pick(row, ['objectId', 'item.name', 'count']);
        item = {
          name: item.item.name,
          objectId: item.objectId,
          count: item.count,
        };
        return item;
      } else {
        showWarning('Cannot select already refunded items');
        return;
      }
    });
    return setSelectedForRefundRows(selectedRecords);
  }, [selectedRowsForRefund]);

  const isRowSelected = (itemId: string) => {
    let selected = false;
    if (selectedRowsForRefund?.length) {
      // selected = _.find(selectedRowsForRefund[itemCategory], ['orderItemId', itemId]);
      selected = selectedRowsForRefund.includes(itemId);
    }
    return !!selected;
  };

  const itemsWithSanitizedInventoryAddress = useMemo(()=>{
    return sanitizeInventoryItemsWithInventoryAddress(itemsArray);
  }, [itemsArray]);

  return (
    <>
      <div className={styles['category-container']}>
        {categoryImage && (
          <img src={categoryImage} />
        )}
        {/* <div className={styles['category-heading']}>{itemCategory}</div> */}
      </div>
      <div className={styles['table-container']}>
        <DataTable
          noHeader={true}
          columns={itemTableColumns}
          customStyles={tableStyles}
          data={itemsWithSanitizedInventoryAddress}
          selectableRows={itemsSelectable}
          onSelectedRowsChange={setSelectedItems}
          selectableRowSelected={row => isRowSelected(row.objectId)}
          selectableRowDisabled={row => row.status === ORDER_STATUS.REFUNDED}
          conditionalRowStyles={[{
            when: row => (row.status === ORDER_ITEM_STATUS.PACKED || newlyPackedItemIds?.includes(row?.objectId)),
            style: {
              background: 'rgb(181 230 195)',
            },
          }]}
        />
      </div>
    </>
  );
};

export default React.memo(OrderItems, propsEqual);